import React, { useState, useEffect } from 'react';
import { zonesAPI, dnssecAPI, sslAPI } from '../services/api';

function DnssecSslInfo({ zoneId }) {
    const [dnssec, setDnssec] = useState(null);
    const [ssl, setSsl] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.allSettled([
            dnssecAPI.getStatus(zoneId),
            sslAPI.getStatus(zoneId),
        ]).then(([dRes, sRes]) => {
            if (dRes.status === 'fulfilled' && dRes.value.data.success) setDnssec(dRes.value.data.dnssec);
            if (sRes.status === 'fulfilled' && sRes.value.data.success) setSsl(sRes.value.data.ssl);
        }).finally(() => setLoading(false));
    }, [zoneId]);

    if (loading) return <div style={{ padding: '1rem', color: '#6b7280', fontSize: '0.85rem' }}>Carregando seguranca...</div>;

    return (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '1rem' }}>
            <div className="card" style={{ margin: 0 }}>
                <h4 style={{ marginBottom: '0.75rem', color: '#374151' }}>DNSSEC</h4>
                {dnssec ? (
                    <div style={{ fontSize: '0.85rem', lineHeight: '1.8' }}>
                        <p><strong>Status:</strong>{' '}
                            <span className={`badge ${dnssec.status === 'active' ? 'success' : dnssec.status === 'pending' ? 'pending' : ''}`}>
                                {dnssec.status || 'disabled'}
                            </span>
                        </p>
                        {dnssec.ds && <p><strong>DS Record:</strong> <code style={{ fontSize: '0.7rem', wordBreak: 'break-all' }}>{dnssec.ds}</code></p>}
                        {dnssec.algorithm && <p><strong>Algoritmo:</strong> {dnssec.algorithm}</p>}
                    </div>
                ) : (
                    <p style={{ fontSize: '0.85rem', color: '#6b7280' }}>Nao disponivel</p>
                )}
            </div>

            <div className="card" style={{ margin: 0 }}>
                <h4 style={{ marginBottom: '0.75rem', color: '#374151' }}>SSL/TLS</h4>
                {ssl ? (
                    <div style={{ fontSize: '0.85rem', lineHeight: '1.8' }}>
                        <p><strong>Modo:</strong>{' '}
                            <span className={`badge ${ssl.mode?.value === 'full' || ssl.mode?.value === 'strict' ? 'success' : 'pending'}`}>
                                {ssl.mode?.value || 'off'}
                            </span>
                        </p>
                        {ssl.verification && Array.isArray(ssl.verification) && ssl.verification.length > 0 && (
                            <p><strong>Certificado:</strong>{' '}
                                <span className={`badge ${ssl.verification[0].certificate_status === 'active' ? 'success' : 'pending'}`}>
                                    {ssl.verification[0].certificate_status || 'unknown'}
                                </span>
                            </p>
                        )}
                        {ssl.mode?.value === 'flexible' && (
                            <p style={{ fontSize: '0.75rem', color: '#f59e0b', marginTop: '0.5rem' }}>
                                Recomendado: Atualize para Full (Strict) para maior seguranca.
                            </p>
                        )}
                    </div>
                ) : (
                    <p style={{ fontSize: '0.85rem', color: '#6b7280' }}>Nao disponivel</p>
                )}
            </div>
        </div>
    );
}

const ZoneDetails = ({ zone, onClose, onUpdate, onManageDNS }) => {
    const [activeTab, setActiveTab] = useState('overview');
    const [settings, setSettings] = useState(null);
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null);
    const [editingSettings, setEditingSettings] = useState({});
    const [savingSettings, setSavingSettings] = useState(false);

    // Categorize settings
    const categorizeSettings = (settingsArray) => {
        const categories = {
            security: {
                title: '🔒 Segurança',
                settings: ['ssl', 'min_tls_version', 'tls_1_3', 'always_use_https', 'automatic_https_rewrites',
                          'security_level', 'challenge_ttl', 'browser_check', 'privacy_pass', 'waf',
                          'security_header', 'tls_client_auth', 'opportunistic_encryption']
            },
            performance: {
                title: '⚡ Performance',
                settings: ['brotli', 'http2', 'http3', 'early_hints', 'minify', 'rocket_loader',
                          'mirage', 'polish', 'webp', 'prefetch_preload', '0rtt', 'websockets']
            },
            caching: {
                title: '💾 Cache',
                settings: ['cache_level', 'browser_cache_ttl', 'edge_cache_ttl', 'always_online',
                          'development_mode', 'sort_query_string_for_cache']
            },
            network: {
                title: '🌐 Rede',
                settings: ['ipv6', 'ip_geolocation', 'pseudo_ipv4', 'cname_flattening',
                          'orange_to_orange', 'proxy_read_timeout', 'response_buffering']
            },
            other: {
                title: '⚙️ Outras Configurações',
                settings: []
            }
        };

        const categorized = {};
        const usedSettings = new Set();

        // Group settings into categories
        Object.keys(categories).forEach(catKey => {
            categorized[catKey] = {
                ...categories[catKey],
                items: []
            };
        });

        settingsArray.forEach(setting => {
            let assigned = false;

            for (const catKey in categories) {
                if (categories[catKey].settings.includes(setting.id)) {
                    categorized[catKey].items.push(setting);
                    usedSettings.add(setting.id);
                    assigned = true;
                    break;
                }
            }

            // If not assigned, put in "other"
            if (!assigned) {
                categorized.other.items.push(setting);
            }
        });

        return categorized;
    };

    // Editable settings configuration
    const editableSettings = {
        'always_use_https': { type: 'toggle', label: 'Sempre Usar HTTPS' },
        'automatic_https_rewrites': { type: 'toggle', label: 'Reescritas Automáticas HTTPS' },
        'brotli': { type: 'toggle', label: 'Compressão Brotli' },
        'development_mode': { type: 'toggle', label: 'Modo de Desenvolvimento' },
        'minify': { type: 'object', label: 'Minificação' },
        'rocket_loader': { type: 'toggle', label: 'Rocket Loader' },
        'browser_cache_ttl': {
            type: 'select',
            label: 'TTL Cache do Navegador',
            options: [
                { value: 1800, label: '30 minutos' },
                { value: 3600, label: '1 hora' },
                { value: 7200, label: '2 horas' },
                { value: 14400, label: '4 horas' },
                { value: 28800, label: '8 horas' },
                { value: 43200, label: '12 horas' },
                { value: 86400, label: '1 dia' },
                { value: 259200, label: '3 dias' },
                { value: 604800, label: '1 semana' },
                { value: 2592000, label: '1 mês' }
            ]
        },
        'security_level': {
            type: 'select',
            label: 'Nível de Segurança',
            options: [
                { value: 'off', label: 'Desligado' },
                { value: 'essentially_off', label: 'Essencialmente Desligado' },
                { value: 'low', label: 'Baixo' },
                { value: 'medium', label: 'Médio' },
                { value: 'high', label: 'Alto' },
                { value: 'under_attack', label: 'Sob Ataque' }
            ]
        },
        'ssl': {
            type: 'select',
            label: 'Modo SSL/TLS',
            options: [
                { value: 'off', label: 'Off' },
                { value: 'flexible', label: 'Flexível' },
                { value: 'full', label: 'Full' },
                { value: 'strict', label: 'Full (Strict)' }
            ]
        }
    };

    useEffect(() => {
        if (activeTab === 'settings') {
            loadSettings();
        }
        // Don't auto-load analytics due to potential API limitations
    }, [activeTab]);

    const loadSettings = async () => {
        try {
            setLoading(true);
            const response = await zonesAPI.getSettings(zone.id);
            setSettings(response.data.settings);
        } catch (error) {
            setMessage({ type: 'error', text: `Erro ao carregar configurações: ${error.message}` });
        } finally {
            setLoading(false);
        }
    };

    const loadAnalytics = async () => {
        try {
            setLoading(true);
            setMessage(null); // Clear any previous messages
            const response = await zonesAPI.getAnalytics(zone.id);
            setAnalytics(response.data.analytics);
            setMessage({ type: 'success', text: 'Analytics carregados com sucesso!' });
        } catch (error) {
            // Don't show error message at top - show in analytics tab instead
            setAnalytics(null);
            console.error('Analytics error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleExport = async (format) => {
        try {
            const response = await zonesAPI.exportRecords(zone.id, format);

            if (format === 'json') {
                const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${zone.name}-dns-records.json`;
                a.click();
            }

            setMessage({ type: 'success', text: `Registros exportados em ${format.toUpperCase()}!` });
        } catch (error) {
            setMessage({ type: 'error', text: `Erro ao exportar: ${error.message}` });
        }
    };

    const handlePurgeCache = async () => {
        if (!window.confirm('Tem certeza que deseja limpar todo o cache desta zona?')) {
            return;
        }

        try {
            await zonesAPI.purgeCache(zone.id, { purge_everything: true });
            setMessage({ type: 'success', text: 'Cache limpo com sucesso!' });
        } catch (error) {
            setMessage({ type: 'error', text: `Erro ao limpar cache: ${error.message}` });
        }
    };

    const handleSettingChange = (settingId, value) => {
        setEditingSettings(prev => ({
            ...prev,
            [settingId]: value
        }));
    };

    const handleSaveSetting = async (settingId) => {
        const value = editingSettings[settingId];
        if (value === undefined) return;

        try {
            setSavingSettings(true);
            await zonesAPI.updateSetting(zone.id, settingId, value);
            setMessage({ type: 'success', text: `Configuração "${settingId}" atualizada com sucesso!` });

            // Update local settings
            setSettings(prev => prev.map(s =>
                s.id === settingId ? { ...s, value } : s
            ));

            // Remove from editing
            setEditingSettings(prev => {
                const newState = { ...prev };
                delete newState[settingId];
                return newState;
            });
        } catch (error) {
            setMessage({ type: 'error', text: `Erro ao atualizar: ${error.response?.data?.errors?.[0]?.message || error.message}` });
        } finally {
            setSavingSettings(false);
        }
    };

    const handleDeleteZone = async () => {
        if (!window.confirm(`Tem certeza que deseja DELETAR a zona ${zone.name}? Esta ação não pode ser desfeita!`)) {
            return;
        }

        const confirmText = window.prompt(`Digite "${zone.name}" para confirmar a exclusão:`);
        if (confirmText !== zone.name) {
            alert('Nome não confere. Exclusão cancelada.');
            return;
        }

        try {
            await zonesAPI.delete(zone.id);
            setMessage({ type: 'success', text: 'Zona deletada com sucesso!' });
            setTimeout(() => {
                onUpdate();
                onClose();
            }, 1500);
        } catch (error) {
            setMessage({ type: 'error', text: `Erro ao deletar zona: ${error.message}` });
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px' }}>
                <div className="modal-header">
                    <div style={{ flex: 1 }}>
                        <h2 className="modal-title" style={{ marginBottom: '0.75rem' }}>🌐 Detalhes da Zona - {zone.name}</h2>
                        {onManageDNS && (
                            <button
                                className="btn btn-primary btn-sm"
                                onClick={onManageDNS}
                            >
                                📝 Gerenciar Registros DNS
                            </button>
                        )}
                    </div>
                    <button className="close-btn" onClick={onClose}>×</button>
                </div>

                {message && (
                    <div className={message.type === 'success' ? 'success-message' : 'error'}>
                        {message.text}
                        <button onClick={() => setMessage(null)} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
                    </div>
                )}

                <div className="tabs" style={{ marginBottom: '1.5rem' }}>
                    <button className={`tab ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>
                        📋 Visão Geral
                    </button>
                    <button className={`tab ${activeTab === 'settings' ? 'active' : ''}`} onClick={() => setActiveTab('settings')}>
                        ⚙️ Configurações
                    </button>
                    <button className={`tab ${activeTab === 'analytics' ? 'active' : ''}`} onClick={() => setActiveTab('analytics')}>
                        📊 Analytics
                    </button>
                    <button className={`tab ${activeTab === 'actions' ? 'active' : ''}`} onClick={() => setActiveTab('actions')}>
                        🔧 Ações
                    </button>
                </div>

                {activeTab === 'overview' && (
                    <div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                            <div className="card" style={{ margin: 0 }}>
                                <h4 style={{ marginBottom: '0.75rem', color: '#374151' }}>Informações Básicas</h4>
                                <div style={{ fontSize: '0.875rem', lineHeight: '1.8' }}>
                                    <p><strong>ID:</strong> <code style={{ fontSize: '0.75rem' }}>{zone.id}</code></p>
                                    <p><strong>Status:</strong> <span className={`badge ${zone.status === 'active' ? 'success' : 'pending'}`}>{zone.status}</span></p>
                                    <p><strong>Criado em:</strong> {new Date(zone.created_on).toLocaleString('pt-BR')}</p>
                                    <p><strong>Modificado em:</strong> {new Date(zone.modified_on).toLocaleString('pt-BR')}</p>
                                </div>
                            </div>

                            <div className="card" style={{ margin: 0 }}>
                                <h4 style={{ marginBottom: '0.75rem', color: '#374151' }}>Nameservers</h4>
                                <ul className="nameservers-list" style={{ marginTop: '0.5rem' }}>
                                    {zone.nameservers.map((ns, idx) => (
                                        <li key={idx} style={{ background: '#d1fae5', color: '#065f46' }}>{ns}</li>
                                    ))}
                                </ul>
                                <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem' }}>
                                    Configure estes nameservers no seu registrador de domínios
                                </p>
                            </div>
                        </div>

                        {/* DNSSEC + SSL Info */}
                        <DnssecSslInfo zoneId={zone.id} />
                    </div>
                )}

                {activeTab === 'settings' && (
                    <div>
                        {loading ? (
                            <div className="loading"><div className="spinner"></div><span>Carregando configurações...</span></div>
                        ) : settings ? (
                            <div>
                                <div style={{
                                    padding: '0.75rem 1rem',
                                    background: '#eff6ff',
                                    border: '1px solid #bfdbfe',
                                    borderRadius: '6px',
                                    marginBottom: '1.5rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem'
                                }}>
                                    <span style={{ fontSize: '1.25rem' }}>ℹ️</span>
                                    <span style={{ fontSize: '0.875rem', color: '#1e40af' }}>
                                        Algumas configurações podem ser editadas. Clique no ícone de edição para alterar.
                                    </span>
                                </div>

                                {Object.entries(categorizeSettings(settings)).map(([catKey, category]) => {
                                    if (category.items.length === 0) return null;

                                    return (
                                        <div key={catKey} style={{ marginBottom: '2rem' }}>
                                            <h3 style={{
                                                fontSize: '1.125rem',
                                                fontWeight: 600,
                                                color: '#111827',
                                                marginBottom: '1rem',
                                                paddingBottom: '0.5rem',
                                                borderBottom: '2px solid #e5e7eb'
                                            }}>
                                                {category.title}
                                            </h3>

                                            <div className="settings-grid">
                                                {category.items.map((setting, idx) => {
                                                    const value = setting.value;
                                                    const isObject = typeof value === 'object' && value !== null;
                                                    const isBoolean = typeof value === 'boolean' || value === 'on' || value === 'off';
                                                    const isEditable = editableSettings[setting.id];
                                                    const isEditing = editingSettings.hasOwnProperty(setting.id);
                                                    const editValue = isEditing ? editingSettings[setting.id] : value;

                                                    return (
                                                        <div key={idx} className={`setting-item ${isEditable ? 'editable' : ''}`}>
                                                            <div className="setting-header">
                                                                <div className="setting-name">
                                                                    {isEditable ? isEditable.label : setting.id.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                                </div>
                                                                {isEditable && !isEditing && (
                                                                    <button
                                                                        className="btn-edit-setting"
                                                                        onClick={() => handleSettingChange(setting.id, value)}
                                                                        title="Editar configuração"
                                                                    >
                                                                        ✏️
                                                                    </button>
                                                                )}
                                                            </div>

                                                            <div className="setting-value">
                                                                {isEditing && isEditable ? (
                                                                    <div className="setting-edit-controls">
                                                                        {isEditable.type === 'toggle' && (
                                                                            <select
                                                                                className="setting-edit-select"
                                                                                value={editValue === 'on' || editValue === true ? 'on' : 'off'}
                                                                                onChange={(e) => handleSettingChange(setting.id, e.target.value)}
                                                                            >
                                                                                <option value="on">Ativo</option>
                                                                                <option value="off">Inativo</option>
                                                                            </select>
                                                                        )}
                                                                        {isEditable.type === 'select' && (
                                                                            <select
                                                                                className="setting-edit-select"
                                                                                value={editValue}
                                                                                onChange={(e) => handleSettingChange(setting.id, e.target.value)}
                                                                            >
                                                                                {isEditable.options.map(opt => (
                                                                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                                                ))}
                                                                            </select>
                                                                        )}
                                                                        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                                                                            <button
                                                                                className="btn btn-primary btn-sm"
                                                                                onClick={() => handleSaveSetting(setting.id)}
                                                                                disabled={savingSettings}
                                                                            >
                                                                                {savingSettings ? '⏳' : '💾'} Salvar
                                                                            </button>
                                                                            <button
                                                                                className="btn btn-secondary btn-sm"
                                                                                onClick={() => {
                                                                                    const newState = { ...editingSettings };
                                                                                    delete newState[setting.id];
                                                                                    setEditingSettings(newState);
                                                                                }}
                                                                            >
                                                                                ✕ Cancelar
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <>
                                                                        {isBoolean ? (
                                                                            <span className={`setting-badge ${(value === 'on' || value === true) ? 'on' : 'off'}`}>
                                                                                {(value === 'on' || value === true) ? '✓ Ativo' : '✗ Inativo'}
                                                                            </span>
                                                                        ) : isObject ? (
                                                                            <code className="setting-code">{JSON.stringify(value)}</code>
                                                                        ) : (
                                                                            <span className="setting-text">{String(value)}</span>
                                                                        )}
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div style={{
                                textAlign: 'center',
                                padding: '3rem',
                                color: '#6b7280'
                            }}>
                                <p style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>⚙️</p>
                                <p>Clique na aba Settings para carregar as configurações</p>
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'analytics' && (
                    <div>
                        {loading ? (
                            <div className="loading"><div className="spinner"></div><span>Carregando analytics...</span></div>
                        ) : analytics ? (
                            <div>
                                <div style={{
                                    padding: '0.75rem 1rem',
                                    background: '#d1fae5',
                                    border: '1px solid #6ee7b7',
                                    borderRadius: '6px',
                                    marginBottom: '1.5rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem'
                                }}>
                                    <span style={{ fontSize: '1.25rem' }}>✓</span>
                                    <span style={{ fontSize: '0.875rem', color: '#065f46', fontWeight: 600 }}>
                                        Analytics carregados com sucesso (últimos 7 dias)
                                    </span>
                                </div>

                                <div style={{
                                    background: '#f9fafb',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '8px',
                                    padding: '1rem',
                                    maxHeight: '500px',
                                    overflowY: 'auto'
                                }}>
                                    <pre style={{
                                        margin: 0,
                                        fontSize: '0.875rem',
                                        lineHeight: '1.6',
                                        color: '#374151',
                                        fontFamily: "'Courier New', monospace"
                                    }}>
                                        {JSON.stringify(analytics, null, 2)}
                                    </pre>
                                </div>
                            </div>
                        ) : (
                            <div>
                                <div style={{
                                    padding: '0.75rem 1rem',
                                    background: '#fef3c7',
                                    border: '1px solid #fcd34d',
                                    borderRadius: '6px',
                                    marginBottom: '1.5rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem'
                                }}>
                                    <span style={{ fontSize: '1.25rem' }}>⚠️</span>
                                    <span style={{ fontSize: '0.875rem', color: '#92400e' }}>
                                        Analytics podem não estar disponíveis dependendo do plano Cloudflare
                                    </span>
                                </div>

                                <div style={{
                                    textAlign: 'center',
                                    padding: '3rem',
                                    background: '#f9fafb',
                                    borderRadius: '8px',
                                    border: '1px solid #e5e7eb'
                                }}>
                                    <p style={{ fontSize: '3rem', marginBottom: '1rem' }}>📊</p>
                                    <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: '#111827', marginBottom: '0.5rem' }}>
                                        Analytics da Zona
                                    </h3>
                                    <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1.5rem' }}>
                                        Visualize estatísticas de tráfego, requisições e performance da sua zona
                                    </p>
                                    <button
                                        className="btn btn-primary"
                                        onClick={() => loadAnalytics()}
                                    >
                                        📊 Carregar Analytics
                                    </button>
                                    <p style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '1rem' }}>
                                        Nota: Requer plano Pro ou superior do Cloudflare
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'actions' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        <div className="card" style={{ margin: 0 }}>
                            <h4 style={{ marginBottom: '0.75rem' }}>📥 Exportar Registros DNS</h4>
                            <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem' }}>
                                Baixe todos os registros DNS em diferentes formatos
                            </p>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <button className="btn btn-secondary btn-sm" onClick={() => handleExport('json')}>
                                    JSON
                                </button>
                                <button className="btn btn-secondary btn-sm" onClick={() => handleExport('csv')}>
                                    CSV
                                </button>
                                <button className="btn btn-secondary btn-sm" onClick={() => handleExport('bind')}>
                                    BIND
                                </button>
                            </div>
                        </div>

                        <div className="card" style={{ margin: 0 }}>
                            <h4 style={{ marginBottom: '0.75rem' }}>🗑️ Limpar Cache</h4>
                            <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem' }}>
                                Remove todo o cache da CDN Cloudflare para esta zona
                            </p>
                            <button className="btn btn-secondary" onClick={handlePurgeCache}>
                                Limpar Todo o Cache
                            </button>
                        </div>

                        <div className="card" style={{ margin: 0, borderLeft: '4px solid #ef4444' }}>
                            <h4 style={{ marginBottom: '0.75rem', color: '#dc2626' }}>⚠️ Zona de Perigo</h4>
                            <p style={{ fontSize: '0.875rem', color: '#991b1b', marginBottom: '1rem' }}>
                                Deletar esta zona removerá permanentemente todos os registros DNS. Esta ação não pode ser desfeita!
                            </p>
                            <button className="btn btn-danger" onClick={handleDeleteZone}>
                                🗑️ Deletar Zona
                            </button>
                        </div>
                    </div>
                )}

                <div className="action-buttons">
                    <button className="btn btn-secondary" onClick={onClose}>
                        Fechar
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ZoneDetails;
